import React, { useState, useEffect } from 'react'
import { useNavigate } from "react-router-dom";
import { Form, Button, Row, Table, InputGroup } from "react-bootstrap";
import { ToastContainer } from 'react-toastify';
import { showToast } from '../reusables/CommonFunction';
import axiosInstance from '../../utils/axios';
import { formatDisplayDate } from '../../utils/formatter';
import ModalDelete from '../reusables/ModalDelete';
import CustomPagination from '../reusables/CustomPagination';
import BusyLoaderOverlay from '../reusables/BusyLoaderOverlay';

const ClinicList = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [clinicName, setClinicName] = useState("");
    const [isModalOpen, setIsModalOpen] = useState({});
    const [clinicDetails, setClinicDetails] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const pageSize = parseInt(process.env.REACT_APP_PAGINATION_PAGE_SIZE);

    const getClinicDetails = (pageNumber) => {
        setLoading(true);
        axiosInstance
            .get('/clinics/', {
                params: {
                    page: pageNumber,
                    q: clinicName,
                },
            })
            .then((response) => {
                if (response.status === 200) {
                    setClinicDetails(response.data.data);
                    setTotalCount(response.data.count);
                }
            })
            .catch(() => {
                showToast("error", "Failed to fetch clinic details");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const onPressSearchClinic = () => {
        setCurrentPage(1); // Reset to the first page
        getClinicDetails(1);
    };

    const onPressReAccountVerification = (oEvent, pClinicId) => {
        oEvent.preventDefault();
        setLoading(true);
        axiosInstance
            .post('/clinics/clinic_reverification_email/', { clinic_id: pClinicId })
            .then(() => {
                showToast("success", "Reverification email sent successfully");
            })
            .catch(() => {
                showToast("error", "Failed to send reverification email");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const onPressEditClinic = (oEvent, pClinicId) => {
        oEvent.preventDefault();
        navigate(`/clinic/${pClinicId}`)
    }

    const onPressDeleteClinic = (oEvent, pClinicId) => {
        oEvent.preventDefault();
        setIsModalOpen({ ['isModalOpen_' + pClinicId]: true })
    }

    const onPressSubmitDeleteClinic = (oEvent, pClinicId) => {
        oEvent.preventDefault();
        setLoading(true);
        axiosInstance
            .delete(`/clinics/${pClinicId}/`)
            .then(response => {
                if (response.status === 200) {
                    showToast("success", response.data.message);
                    setIsModalOpen({ ['isModalOpen_' + pClinicId]: false })
                    setCurrentPage(1); // Reset to the first page
                    getClinicDetails(1)
                }
            })
            .catch(error => {
                showToast("error", "Failed to delete clinic");
            })
            .finally(() => {
                setLoading(false);
            });
    }

    useEffect(() => {
        getClinicDetails(currentPage);
    }, [currentPage]);

    return (
        <Row className="ml-3 mr-3 mb-3">
            <div className='col-md-12'>
                <InputGroup className="mb-3 col-md-4 pl-0" >
                    <Form.Control
                        type="text"
                        name="clinicName"
                        onChange={(event) => setClinicName(event.target.value)}
                        placeholder="Enter Clinic Name..."
                        value={clinicName}
                    />
                    <Button type="submit" variant="outline-secondary" onClick={onPressSearchClinic}>
                        Search
                    </Button>
                </InputGroup>

                {/* If the count is 0 then hide the table */}
                {clinicDetails.length > 0 ?
                    <>
                        <Table bordered hover responsive>
                            <thead light="true">
                                <tr>
                                    <th style={{ width: "2rem" }}>#</th>
                                    <th style={{ width: "20rem" }}>Clinic Name</th>
                                    <th style={{ width: "10rem" }}>Doctor Name</th>
                                    <th style={{ width: "8rem" }}>Mobile Number</th>
                                    <th style={{ width: "6rem" }}>Plan Type</th>
                                    <th style={{ width: "6rem" }}>Start Date</th>
                                    <th style={{ width: "6rem" }}>Expiring At</th>
                                    <th style={{ width: "6rem" }}>Account Verification</th>
                                    <th style={{ width: "7rem" }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {clinicDetails.map((clinic, index) => {
                                    return [
                                        <tr key={clinic.clinic_id}>
                                            <td>{(currentPage - 1) * pageSize + index + 1}</td>
                                            <td>{clinic.clinic_name}</td>
                                            <td>{clinic.doctor_name}</td>
                                            <td>{clinic.mobile_number}</td>
                                            <td>{clinic.plan_type === 'F' ? 'Free Trial' : 'Paid Plan'}</td>
                                            <td>{formatDisplayDate(clinic.start_date)}</td>
                                            <td>{formatDisplayDate(clinic.expiring_date)}</td>
                                            <td>{clinic.is_active ?
                                                <Button type='button' variant='success' size='sm'>Completed</Button> :
                                                <Button type='button' variant='danger' size='sm' onClick={oEvent => onPressReAccountVerification(oEvent, clinic.clinic_id)} >Pending</Button>
                                            }
                                            </td>
                                            <td>
                                                <Row>
                                                    <div className='col-6'>
                                                        <Button type='button' variant='info' size='sm' onClick={oEvent => onPressEditClinic(oEvent, clinic.clinic_id)} >Edit</Button>
                                                    </div>
                                                    <div className='col-6 pl-0'>
                                                        <Button type='button' variant='danger' size='sm' onClick={oEvent => onPressDeleteClinic(oEvent, clinic.clinic_id)}>Delete</Button>
                                                        <ModalDelete show={isModalOpen['isModalOpen_' + clinic.clinic_id]} modalTitle="Clinic" modalBody={clinic.clinic_name}
                                                            onHide={() => setIsModalOpen({ ['isModalOpen_' + clinic.clinic_id]: false })}
                                                            onSubmit={oEvent => onPressSubmitDeleteClinic(oEvent, clinic.clinic_id)} />
                                                    </div>
                                                </Row>
                                            </td>
                                        </tr>
                                    ]
                                })}
                            </tbody>
                        </Table>

                        <CustomPagination
                            totalCount={totalCount}
                            currentPage={currentPage}
                            pageSize={pageSize}
                            onPageChange={setCurrentPage} />
                    </>
                    : <> </>}
                <ToastContainer />
                <BusyLoaderOverlay show={loading} />
            </div>
        </Row>
    )
}

export default ClinicList